import React, { useEffect, useState, useRef } from 'react';
import { Row, Form, Button, Card, Col, Alert, Spinner } from 'react-bootstrap';
import { PopupsForAddingState, PopupsState, ReturnedPortfolio } from '../Create-Portfolio/types';
import '../Preview/Preview.css';
import { maxDate, minDate } from '../../config/calendarMinMaxDates';
import FileUploadComponent from '../FileUploadButton/SingleFileUploadButton';
import { formatDate, handleNewItemFields } from '../UserProfile/helpers';
import { validationMessages } from '../../config/validationMessages';
import { newlyAddedCertification } from '../Create-Portfolio/initialValues';
//import { ReactComponent as DeleteIcon } from '../../images/trash3-fill.svg';
import { v4 as uuidv4 } from 'uuid';
import { API_ROUTES } from '../../config/api-routes';
import axiosInstance from '../../api/api';
import { toast } from 'react-toastify';
import { refreshPortfolioAfterSave, type PortfolioGetDataFn } from '../../utils/portfolioRefresh';

type ProjectFile = string | { name: string; tempFile: File };

interface Props {
  portfolioId?: number;
  index: number;
  portfolio: ReturnedPortfolio;
  setOpenedPopups: React.Dispatch<React.SetStateAction<PopupsState>>;
  setPopupsToAdd: React.Dispatch<React.SetStateAction<PopupsForAddingState>>;
  setPortfolioData: React.Dispatch<React.SetStateAction<ReturnedPortfolio>>;
  setSavePortfolioButton: React.Dispatch<React.SetStateAction<boolean>>;
  savePortfolioButton: boolean;
  popupVariant: string;
  getData?: PortfolioGetDataFn;
}

interface Certification {
  id?: string | number;
  name: string;
  date: string;
  filesUrl: ProjectFile[];
  // …other fields
}

const CertificationsPopup: React.FC<Props> = ({
  portfolioId,
  index,
  portfolio,
  setOpenedPopups,
  setPopupsToAdd,
  setPortfolioData,
  setSavePortfolioButton,
  savePortfolioButton,
  getData,
  popupVariant,
}) => {
  const [certifications, setCertifications] = useState<Certification[]>(
    portfolio.certifications as Certification[]
  );
  const [certification, setNewCertification] = useState<any>(newlyAddedCertification);
  const [disabled, setDisabled] = useState<boolean>(true);
  //const [success, setAddingFileSuccess] = useState<boolean>(false);

  const popupRef = useRef<HTMLDivElement>(null);

  const [sizeError, setSizeError] = useState<boolean>(false);
  const [fileError, setFileError] = useState<boolean>(false);

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadDisabled, setUploadDisabled] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setCertifications(portfolio.certifications as Certification[]);
  }, [portfolio.certifications]);

  const handleSelectFileForNew = (file: File) => {
    const maxSizeInBytes = 10 * 1024 * 1024;
    if (file.size >= maxSizeInBytes) {
      setSizeError(true);
      return;
    }
    setPendingFiles((prev) => [...prev, file]);
    setSizeError(false);
    setUploadDisabled((prev) => {
      return { ...prev, [index]: true };
    });
  };

  const removePendingFile = (i: number) => {
    setPendingFiles((prev) => prev.filter((_, idx) => idx !== i));
    setUploadDisabled((prev) => ({ ...prev, [index]: false })); // ✅ re-enable
  };

  const handleCertificationSubmit = (e: React.FormEvent<HTMLFormElement>, index: number) => {
    e.preventDefault();

    if (certifications?.length === undefined) {
      return;
    }

    if (certifications?.length === 0) {
      setFileError(true);
      return;
    }

    // Don’t merge files here – just update other fields
    const updatedProjects = [...certifications];
    updatedProjects[index] = {
      ...updatedProjects[index],
      filesUrl: [
        ...(updatedProjects[index].filesUrl || []),
        ...pendingFiles.map((f) => f.name), // only strings
      ],
    };

    setCertifications(updatedProjects);

    // keep pendingFiles separate, do not clear yet
    setPortfolioData((prev) => ({
      ...prev,
      certifications: updatedProjects as typeof prev.certifications,
    }));

    setDisabled(false);

    setOpenedPopups((prev) => {
      const newProjectsPopups = [...prev.certifications];
      newProjectsPopups[index] = false;
      return { ...prev, certifications: newProjectsPopups };
    });
    setPendingFiles([]);
    setSavePortfolioButton(true);
  };

  const handleSubmitOfNewCertification = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Track old ids first
      const existingIds = new Set((portfolio.certifications || []).map((p: any) => p.id));

      // Stage the accomplishment
      const stagedAcc = {
        clientUuid: uuidv4(), // 🔑 temp unique key
        id: undefined,
        name: certification.name?.trim(),
        date: certification.date ? new Date(certification.date).toISOString() : null,
        filesUrl: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (!stagedAcc.name) {
        toast.error('Certification name is required');
        return;
      }

      // Build payload (plural ✅)
      const info = {
        ...portfolio,
        certifications: [...(portfolio.certifications || []), stagedAcc],
      };

      // Send payload
      const form = new FormData();
      form.append('info', JSON.stringify(info));

      const url = `${API_ROUTES.PORTFOLIO.EDIT_PORTFOLIO}/${portfolioId}`;
      const saveRes = await axiosInstance.post(url, form);

      if (!saveRes || saveRes.status >= 300) {
        throw new Error(`Save failed (${saveRes?.status})`);
      }

      const savedPortfolio = saveRes.data.portfolio;

      // ✅ Find the cert with a *new id*
      const created = savedPortfolio.certifications.find(
        (p: any) => !existingIds.has(p.id) && p.name === stagedAcc.name
      );

      if (!created?.id) {
        throw new Error('Could not identify newly created accomplishment id');
      }

      // ✅ Upload files only when new id exists
      if (pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          const infoForUpload = {
            certification: { id: created.id, fileId: uuidv4() }, // singular ✅
          };
          const fd = new FormData();
          fd.append('info', JSON.stringify(infoForUpload));
          fd.append(infoForUpload.certification.fileId, file, file.name);
          await axiosInstance.post(API_ROUTES.PORTFOLIO.UPLOAD_FILES, fd);
        }
      }

      toast.success('Certification added');
      setPendingFiles([]);
      setNewCertification(created);
      setPopupsToAdd((prev) => ({ ...prev, certifications: false }));
      /*  setSavePortfolioButton(true);
      setTimeout(() => {
        scrollToPortfolioButton();
      }, 0); */

      await refreshPortfolioAfterSave(setPortfolioData, savedPortfolio, getData);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to add certification');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDeleteFile = (accIndex: number, fileIndex: number, fileItem: any) => {
    if (fileItem instanceof File) {
      // Pending file (remove from pendingFilesMap)
      setPendingFiles((prev) => prev.filter((_, idx) => idx !== fileIndex));
      setUploadDisabled((prev) => ({ ...prev, [accIndex]: false }));
      toast.success('Pending file removed');
    } else {
      // Existing file (mark for deletion in DB)
      const updatedFiles = certifications[accIndex].filesUrl.filter(
        (_: any, idx: number) => idx !== fileIndex
      );
      const updatedItem = {
        ...certifications[accIndex],
        filesUrl: updatedFiles,
      };
      const updatedList = [...certifications];
      updatedList[accIndex] = updatedItem;

      setUploadDisabled((prev) => ({ ...prev, [accIndex]: false }));

      setCertifications(updatedList);
      setDisabled(false);
      setSavePortfolioButton(true);
    }
  };

  const handleInputChange = (
    index: number,
    field: string,
    value: string,
    array: any,
    setItems: React.Dispatch<React.SetStateAction<any>>
  ) => {
    const itemIndex = array.findIndex((_: any, prIndex: number) => prIndex === index);

    if (itemIndex !== -1) {
      const updatedItems = [...array];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        [field]: value,
      };
      setItems(updatedItems);
      setDisabled(false);
      //setSavePortfolioButton(true);
    }
  };

  const handleFileChange = (index: number, file: File) => {
    const maxSizeInBytes = 10 * 1024 * 1024;
    if (file.size >= maxSizeInBytes) {
      setSizeError(true);
    }

    setPendingFiles((prev) => [...prev, file]);

    setDisabled(false);

    setSizeError(false);
    setUploadDisabled((prev) => {
      return { ...prev, [index]: true };
    });
    /* setSavePortfolioButton(true);
    setTimeout(() => {
      scrollToPortfolioButton();
    }, 0); */
  };

  return (
    <Card
      className={`${popupVariant === 'adding' ? '' : 'user-popup'} p-3 popup-card`}
      ref={popupRef}
    >
      <Card.Header className="popup-header">
        <div className="header-icon">📁</div>
        <div>
          <div className="header-title">
            {popupVariant === 'adding' ? 'Add Certification' : 'Edit Certification'}
          </div>
        </div>
      </Card.Header>

      <Card.Body className="scrollable-card">
        {popupVariant === 'adding' ? (
          /* -------------------- ADD CERTIFICATION -------------------- */
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmitOfNewCertification(e);
            }}
          >
            {/* Certification Name */}
            <div className="section">
              <Form.Group as={Row} className="mb-3">
                <Col sm={12}>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Enter a name"
                    onChange={(e) =>
                      handleNewItemFields('name', e.target.value, setNewCertification)
                    }
                  />
                </Col>
              </Form.Group>
            </div>

            {/* Date */}
            <div className="section">
              <Form.Group as={Row} className="mb-3">
                <Col sm={12}>
                  <Form.Control
                    required
                    type="date"
                    onChange={(e) =>
                      handleNewItemFields('date', e.target.value, setNewCertification)
                    }
                    min={minDate}
                    max={maxDate}
                  />
                </Col>
              </Form.Group>
            </div>

            {/* File (ADD) */}
            <div className="section">
              <Form.Group as={Row} className="mb-3">
                <Col sm={12} className="file-upload-section">
                  <div className="file-chip-wrap">
                    {pendingFiles.map((f, i) => (
                      <span key={i} className="file-chip" title={f.name}>
                        <span className="file-dot" />
                        <span className="file-chip-text">
                          {f.name.length > 34
                            ? `${f.name.slice(0, 20)}…${f.name.slice(-10)}`
                            : f.name}
                        </span>
                        <button
                          type="button"
                          className="chip-remove"
                          onClick={() => removePendingFile(i)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>

                  <FileUploadComponent
                    key={pendingFiles.length} /* re-mount after remove */
                    handleFileChange={(file: File) => {
                      if (pendingFiles.length >= 1) {
                        toast.error('You can only upload one file at a time');
                        return;
                      }
                      handleSelectFileForNew(file);
                    }}
                    disabled={pendingFiles.length >= 1} /* add-mode: simple rule */
                  />
                  <small className="text-muted d-block mt-1">Max file size: 10MB</small>

                  {sizeError && (
                    <Alert variant="danger">{validationMessages.FILES_EXCEEDED_LIMIT_10MB}</Alert>
                  )}
                </Col>
              </Form.Group>
            </div>

            {/* Actions */}
            <div className="sticky-actions">
              <Button
                variant="outline-secondary"
                onClick={() => setPopupsToAdd((prev) => ({ ...prev, certifications: false }))}
              >
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </div>
          </Form>
        ) : (
          /* -------------------- EDIT CERTIFICATION -------------------- */
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleCertificationSubmit(e, index);
            }}
          >
            {/* Certification Name */}
            <div className="section">
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={12}>
                  Certification Name
                </Form.Label>
                <Col sm={12}>
                  <Form.Control
                    required
                    type="text"
                    defaultValue={portfolio.certifications[index].name}
                    onChange={(e) =>
                      handleInputChange(
                        index,
                        'name',
                        e.target.value,
                        certifications,
                        setCertifications
                      )
                    }
                  />
                </Col>
              </Form.Group>
            </div>

            {/* Date Received */}
            <div className="section">
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={12}>
                  Date Received
                </Form.Label>
                <Col sm={12}>
                  <Form.Control
                    required
                    type="date"
                    defaultValue={formatDate(portfolio.certifications[index].date)}
                    onChange={(e) =>
                      handleInputChange(
                        index,
                        'date',
                        e.target.value,
                        certifications,
                        setCertifications
                      )
                    }
                    min={minDate}
                    max={maxDate}
                  />
                </Col>
              </Form.Group>
            </div>

            {/* Upload Certificate (EDIT) */}
            <div className="section">
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={12}>
                  Upload Certificate
                </Form.Label>
                <Col sm={12} className="file-upload-section">
                  <div className="file-chip-wrap">
                    {[...(certifications[index].filesUrl || []), ...(pendingFiles || [])].map(
                      (fileItem: any, fileIndex: number) => {
                        const fileName =
                          fileItem?.name ||
                          fileItem?.file?.name ||
                          fileItem.toString().replace(/_/g, ' ').split('/').pop();

                        return (
                          <span key={fileIndex} className="file-chip" title={fileName}>
                            <span className="file-dot" />
                            <span className="file-chip-text">
                              {fileName.length > 34
                                ? `${fileName.slice(0, 20)}…${fileName.slice(-10)}`
                                : fileName}
                            </span>
                            <button
                              type="button"
                              className="chip-remove"
                              onClick={() => handleDeleteFile(index, fileIndex, fileItem)}
                            >
                              ×
                            </button>
                          </span>
                        );
                      }
                    )}
                  </div>

                  {certifications[index].filesUrl.length + pendingFiles.length < 1 && (
                    <div className="select-project-file">
                      <FileUploadComponent
                        handleFileChange={(file: File) => {
                          const total =
                            (certifications[index]?.filesUrl?.length || 0) + pendingFiles.length;
                          if (total >= 1) {
                            toast.error('You can only upload one file at a time');
                            return;
                          }
                          handleFileChange(index, file);
                        }}
                        disabled={uploadDisabled[index]}
                      />
                      <small className="text-muted d-block mt-1">Max file size: 10MB</small>
                    </div>
                  )}

                  {fileError && <Alert variant="danger">{fileError}</Alert>}
                  {sizeError && (
                    <Alert variant="danger">{validationMessages.FILES_EXCEEDED_LIMIT_10MB}</Alert>
                  )}
                </Col>
              </Form.Group>
            </div>

            {/* Actions */}
            <div className="sticky-actions">
              <Button
                variant="outline-secondary"
                onClick={() =>
                  setOpenedPopups((prev) => {
                    const next = [...prev.certifications];
                    next[index] = false;
                    return { ...prev, certifications: next };
                  })
                }
              >
                Cancel
              </Button>
              <Button variant="success" type="submit" disabled={disabled}>
                Submit
              </Button>
            </div>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};

export default CertificationsPopup;
